import React, { useState, useContext, useEffect } from "react";
import {
  Button,
  withStyles,
  MobileContext,
  InputContainer,
  Input,
  TextArea,
  Toggle,
  Checkbox,
  Upload,
  DateInput
} from "components/common";
import {
  Accordion,
  AccordionItem,
  AccordionItemHeading,
  AccordionItemButton,
  AccordionItemPanel
} from "react-accessible-accordion";
import moment from "moment";
import { track } from "../../../utils/analytics";
import { RadioButtons } from "components/common";
import SettingsInputContainer from "../Settings/common/SettingsInputContainer";
import { commafy } from "utils";

const exists = x => !!x || !(x ?? 1);

const disputeFormUrl =
  "https://tomo-public-docs.s3.us-west-1.amazonaws.com/Dispute+Form.pdf";
const disputeEmailUrl = "dispute@tomocredit.com";

const acceptedFileTypes = [
  "image/*",
  "application/pdf",
  "application/msword",
  "application/rtf",
  "text/plain"
];

const commonUploadSettings = {
  fileTypes: acceptedFileTypes,
  multiple: false,
  maxFileSize: 10
};

const config = {
  whatIs: {
    id: 1,
    detail: () => (
      <div className="text-left">
        <div className="font-bold text-xl">What Is a Dispute?</div>
        <div className="pt-2">
          If you believe there is an error with a charge on your credit card
          account, the information on this page will give you some options to
          investigate and try to resolve it.
        </div>
        <div className="pt-2">
          A credit card dispute is a process in which you contest that there is
          a billing error on your account or that a merchant did not honor their
          terms and conditions.
        </div>
        <ul className="pt-2 ml-4 list-disc">
          <li>
            A dispute is not the same as requesting a refund from the merchant,
            which you should do in many situations where you want your money
            back. For example, when you’re dissatisfied with the merchant’s
            policies or service, or when you need to cancel plans. TomoCredit
            can’t issue a refund on behalf of a merchant.
          </li>
        </ul>
        <div className="pt-2">
          If you file a dispute, TomoCredit asks the merchant to review the
          disputed charge and its associated terms and conditions. These are
          usually the same terms and conditions you agreed to when you made the
          purchase.
        </div>
      </div>
    ),
    valid: () => true,
    next: () => "whatHappens"
  },
  whatHappens: {
    id: 2,
    detail: () => (
      <div className="text-left">
        <div className="font-bold text-xl">
          What Happens When You File a Dispute Through TomoCredit
        </div>
        <div className="pt-2">
          It can take more than 60 days for TomoCredit to resolve a dispute with
          the merchant.
        </div>
        <div className="pt-2">
          Here’s an overview of what happens after you file a dispute:
        </div>
        <ul className="pt-2 ml-4 list-disc">
          <li>
            We may request additional documentation from you related to the
            dispute.
          </li>
          <li>
            We send information about the purchase and why you’re disputing it
            to the merchant via Mastercard and the merchant’s bank.
          </li>
          <li>The merchant accepts or rejects the dispute.</li>
          <li>
            If the merchant rejects the dispute, responsibility for the disputed
            amount is based primarily on the merchant’s policies and terms
            concerning refunds or cancellations.
          </li>
          <li>
            If you are responsible, the disputed amount will not be credited to
            your account. If the merchant is found responsible, we will credit
            the disputed amount to your account.
          </li>
        </ul>
        <div className="pt-2">
          At any point in this process, you can reach out to the merchant
          directly to try to resolve your dispute.
        </div>
      </div>
    ),
    valid: () => true,
    next: () => "whatToDo"
  },
  whatToDo: {
    id: 3,
    detail: () => (
      <div className="text-left">
        <div className="font-bold text-xl">
          What To Do If the Disputed Charge Is Not Credited Back to Your Account
        </div>
        <div className="pt-2">
          We understand that having a disputed charge not credited back to your
          account isn’t the outcome you were hoping for. If the merchant
          provides information demonstrating that the original charge is
          valid—or that they gave you a credit for it—we will not credit amount
          to your account and end our investigation.
        </div>
        <div className="pt-2">
          And remember: you can always try to resolve your dispute and get your
          money back by working with the merchant directly.
        </div>
      </div>
    ),
    valid: () => true,
    next: () => "howToFile"
  },
  howToFile: {
    id: 4,
    prompt: "",
    detail: () => (
      <div className="text-left">
        <div className="font-bold text-xl">
          What To Do If the Disputed Charge Is Not Credited Back to Your Account
        </div>
        <div className="pt-2 font-bold">Double-Check Charges</div>
        <div className="pt-2">
          Filing a dispute may not always be the best option. Before you dispute
          a charge, do a quick double-check:
        </div>
        <ul className="pt-2 ml-4 list-disc">
          <li>
            Make sure the charge has posted to your credit card account.
            TomoCredit can't help you dispute a charge until it has posted. The
            easiest way to check this is on our website or mobile app.
          </li>
          <li>
            Check with other people authorized to use the credit card account to
            make sure they didn't make the charge. For example, an authorized
            user or someone else you have given access might have made the
            charge.
          </li>
          <li>
            Check your paper and digital receipts—it’s easy to confuse similar
            charges or forget about tips.
          </li>
        </ul>
        <div className="pt-2 font-bold">Start With the Merchant First</div>
        <div className="pt-2">
          Working directly with the merchant is often the fastest way to resolve
          your dispute. We will usually ask you if you have already done this
          when you contact TomoCredit to file a dispute.
        </div>
        <div className="pt-2">
          If you’re contacting the merchant, you could use a message like this:
        </div>
        <div className="mt-2 px-1 py-2 border rounded italic bg-white">
          <p>Hi [name of company],</p>
          <p style={{ "text-indent": "1em" }}>
            I purchased [item or service] for [price] on [date]. I would like a
            refund of [full $ amount or partial $ amount] because [description
            of problem]. [Optional: I would also like to cancel any future
            services related to this purchase].
          </p>
          <p style={{ "text-indent": "1em" }}>
            Attached are copies of [description of information you are
            including] that document this problem.
          </p>
          <p style={{ "text-indent": "1em" }}>
            Please investigate this and let me know if you need any additional
            information to process this request.
          </p>
          <br />
          <p>Thank you,</p>
          <p>[Your name]</p>
        </div>
        <div className="pt-2 font-bold">
          Filing a Dispute Through TomoCredit
        </div>
        <div className="pt-2">
          You can file a dispute by email. Email{" "}
          <a
            href={disputeFormUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-green-primary"
          >
            this form
          </a>{" "}
          to us at{" "}
          <a href={`mailto:${disputeEmailUrl}`} className="text-green-primary">
            {disputeEmailUrl}
          </a>.
        </div>
      </div>
    ),
    valid: () => true,
    exit: true
  }
};

const summarize = state => {
  const { history, progress } = state;

  const getValue = (p, f, v = "value") => progress[p]?.fields?.[f]?.[v];

  // Determine which pages produce which sets of data
  // Some pages are purely for UX and do not produce data
  const dataIds = {
    recognize: ["recognized"],
    recognizeFollowup: ["recognized"],
    haveCard: ["haveCard"],
    policeReport: ["policeReport", "policeReportFiles"],
    confirmation: null,
    anotherIssue: null,
    thankYou: null,
    issues: ["issue"],
    goodsServices: ["goodsServices", "goodsServicesFiles"],
    goodsServicesDescription: [
      "goodsServicesDescription",
      "goodsServicesDescriptionFiles"
    ],
    merchant: ["merchantContacted"],
    checkWithMerchant: null,
    merchantLastContact: ["merchantContactDate"],
    waitForMerchant: null,
    merchantConversation: ["merchantConversation", "merchantConversationFiles"],
    otherApplicableInformation: ["otherInformation", "otherInformationFiles"],
    review: null,
    duplicatesPending: null,
    waitForPendingDuplicates: null,
    uploadReceipt: ["receipt", "receiptFiles"],
    returnedOrVoided: ["returnedOrVoided"],
    whenReturned: ["returnDate"],
    howReturned: ["returnMethod"],
    uploadReturnReceipt: ["returnReceipt", "returnReceiptFiles"],
    trackingNumber: ["trackingNumber"],
    uploadVoidedReceipt: ["voidedReceipt", "voidedReceiptFiles"],
    correctedAmount: ["correctedAmount"],
    documentation: ["correctedAmountFiles"],
    howCancelled: ["cancellationMethod"],
    haveConfirmation: [
      "cancellationConfirmation",
      "cancellationConfirmationFiles"
    ],
    describeIssue: ["issueDescription"],
    caseDocumentation: ["documentation", "documentationFiles"],
    rememberWhenLostCard: null,
    lostCardDiscoveryDate: ["lostOrStolenDate"],
    lostCardDiscoveryDescription: ["lostOrStolenDescription"]
  };

  // const mediaSteps = [
  //   "policeReportFiles",
  //   "goodsServicesFiles",
  //   "goodsServicesDescriptionFiles",
  //   "merchantConversationFiles",
  //   "otherInformationFiles",
  //   "receiptFiles",
  //   "returnReceiptFiles",
  //   "voidedReceiptFiles",
  //   "correctedAmountFiles",
  //   "cancellationConfirmationFiles",
  //   "documentationFiles"
  // ];

  // Transforms the page data down to discrete data points
  const transforms = {
    recognized: {
      question: "Do you recognize this transaction?",
      label: "Transaction recognized",
      transform: () =>
        getValue("recognizeFollowup", "transaction_recognized_now") ||
        getValue("recognize", "transaction_recognized")
    },
    haveCard: {
      question: "Do you currently have your physical card?",
      label: "Have card",
      transform: () => getValue("haveCard", "have_card")
    },
    policeReport: {
      question: "Have you filed a police report?",
      label: "Filed police report",
      transform: () =>
        getValue("policeReport", "no_report")
          ? "No"
          : !!getValue("policeReport", "police_report")
          ? "Yes"
          : "No"
    },
    policeReportFiles: {
      question: "Please provide a copy of your police report",
      label: "Police report files",
      transform: () => getValue("policeReport", "police_report", "description"),
      media: () => getValue("policeReport", "police_report", "value")
    },
    issue: {
      question: "What is the issue with this transaction?",
      label: "Dispute reason",
      transform: () =>
        ({
          not_as_described: "Goods/services were not as described",
          incorrect_amount: "Incorrect amount charged",
          duplicate: "Duplicate transaction",
          credit_not_processed: "Credit was not processed",
          canceled_recurring: "Recurring transaction was not canceled",
          other: "Other"
        }[getValue("issues", "issue")])
    },
    goodsServices: {
      question: "What were the goods/services purchased?",
      label: "Goods/Services",
      transform: () => getValue("goodsServices", "description")
    },
    goodsServicesFiles: {
      question: "Please provide documentation of the goods/services",
      label: "Goods/Services files",
      transform: () => getValue("goodsServices", "screenshots", "description"),
      media: () => getValue("goodsServices", "screenshots", "value")
    },
    goodsServicesDescription: {
      question: "How were the goods/services not as described?",
      label: "Problem with goods/services",
      transform: () => getValue("goodsServicesDescription", "description")
    },
    goodsServicesDescriptionFiles: {
      question:
        "Please provide documentation of how the goods/services were not as described",
      label: "Problem with goods/services files",
      transform: () =>
        getValue("goodsServicesDescription", "screenshots", "description"),
      media: () => getValue("goodsServicesDescription", "screenshots", "value")
    },
    merchantContacted: {
      question: "Have you contacted the merchant?",
      label: "Contacted merchant",
      transform: () => getValue("merchant", "merchant_contacted")
    },
    merchantContactDate: {
      question: "When did you contact the merchant?",
      label: "Contact date",
      transform: () =>
        moment(getValue("merchantLastContact", "last_contact")).format(
          "MM/DD/YYYY"
        )
    },
    merchantConversation: {
      question: "Describe your conversation with the merchant",
      label: "Merchant conversation",
      transform: () => getValue("merchantConversation", "description")
    },
    merchantConversationFiles: {
      question: "Please provide documentation of the merchant conversation",
      label: "Merchant conversation files",
      transform: () =>
        getValue("merchantConversation", "screenshots", "description"),
      media: () => getValue("merchantConversation", "screenshots", "value")
    },
    otherInformation: {
      question: "Is there any other application information?",
      label: "Other information",
      transform: () => getValue("otherApplicableInformation", "description")
    },
    otherInformationFiles: {
      question: "Please provide documentation of other application information",
      label: "Other files",
      transform: () =>
        getValue("otherApplicableInformation", "screenshots", "description"),
      media: () =>
        getValue("otherApplicableInformation", "screenshots", "value")
    },
    receipt: {
      question: "Do you have a copy of your receipt?",
      label: "Have receipt",
      transform: () =>
        getValue("uploadReceipt", "no_receipt")
          ? "No"
          : !!getValue("uploadReceipt", "screenshots")
          ? "Yes"
          : "No"
    },
    receiptFiles: {
      question: "Please provide a copy of your receipt",
      label: "Receipt files",
      transform: () => getValue("uploadReceipt", "screenshots", "description"),
      media: () => getValue("uploadReceipt", "screenshots", "value")
    },
    returnedOrVoided: {
      question: "Was this a returned or a voided transaction?",
      label: "Returned or voided",
      transform: () => getValue("returnedOrVoided", "returned_or_voided")
    },
    returnDate: {
      question: "When did you return the merchandise?",
      label: "Return date",
      transform: () =>
        moment(getValue("whenReturned", "returned_date")).format("MM/DD/YYYY")
    },
    returnMethod: {
      question: "How did you return the merchandise?",
      label: "Return method",
      transform: () => getValue("howReturned", "how_returned")
    },
    returnReceipt: {
      question: "Do you have a copy of your return receipt?",
      label: "Have return receipt",
      transform: () =>
        getValue("returnReceipt", "no_receipt")
          ? "No"
          : !!getValue("returnReceipt", "screenshots")
          ? "Yes"
          : "No"
    },
    returnReceiptFiles: {
      question: "Please provide a copy of your return receipt",
      label: "Return receipt files",
      transform: () => getValue("returnReceipt", "screenshots", "description"),
      media: () => getValue("returnReceipt", "screenshots", "value")
    },
    trackingNumber: {
      question: "Please provide the tracking number for your return",
      label: "Tracking number",
      transform: () =>
        getValue("trackingNumber", "no_tracking_number")
          ? null
          : getValue("trackingNumber", "tracking_number")
    },
    voidedReceipt: {
      question: "Do you have a copy of your voided receipt?",
      label: "Have void receipt",
      transform: () =>
        getValue("uploadVoidedReceipt", "no_receipt")
          ? "No"
          : !!getValue("uploadVoidedReceipt", "screenshots")
          ? "Yes"
          : "No"
    },
    voidedReceiptFiles: {
      question: "Please provide a copy of your voided receipt",
      label: "Voided receipt files",
      transform: () =>
        getValue("uploadVoidedReceipt", "screenshots", "description"),
      media: () => getValue("uploadVoidedReceipt", "screenshots", "value")
    },
    correctedAmount: {
      question: "What was the amount that should have been billed?",
      label: "Corrected amount",
      transform: () => getValue("correctedAmount", "corrected_amount")
    },
    correctedAmountFiles: {
      question: "Please provide documentation of the correct amount",
      label: "Corrected amount documentation",
      transform: () =>
        getValue("documentation", "documentation", "description"),
      media: () => getValue("documentation", "documentation", "value")
    },
    cancellationMethod: {
      question: "How did you submit your request for cancellation?",
      label: "Cancellation method",
      transform: () => getValue("howCancelled", "method")
    },
    cancellationConfirmation: {
      question: "Do you have confirmation that the charge was cancelled?",
      label: "Cancellation confirmation",
      transform: () =>
        getValue("haveConfirmation", "no_confirmation")
          ? "No"
          : !!getValue("haveConfirmation", "documentation")
          ? "Yes"
          : "No"
    },
    cancellationConfirmationFiles: {
      question: "Please provide documentation of the cancellation",
      label: "Cancellation confirmation files",
      transform: () =>
        getValue("haveConfirmation", "confirmation", "description"),
      media: () => getValue("haveConfirmation", "confirmation", "value")
    },
    issueDescription: {
      question: "Please describe the issue with your transaction.",
      label: "Issue description",
      transform: () => getValue("describeIssue", "description")
    },
    documentation: {
      question:
        "Do you have any relevant documentation that supports your case?",
      label: "Supporting documentation",
      transform: () =>
        getValue("caseDocumentation", "no_documentation")
          ? "No"
          : !!getValue("caseDocumentation", "documentation")
          ? "Yes"
          : "No"
    },
    documentationFiles: {
      question: "Please provide a copy of your receipt",
      label: "Supporting documentation files",
      transform: () =>
        getValue("caseDocumentation", "documentation", "description"),
      media: () => getValue("caseDocumentation", "documentation", "value")
    },
    lostOrStolenDate: {
      question: "When did you lose your card?",
      label: "Date card was lost",
      transform: () => getValue("lostCardDiscoveryDate", "lost_date")
    },
    lostOrStolenDescription: {
      question: "How did the card leave your possession?",
      label: "Circumstances around card loss",
      transform: () =>
        getValue("lostCardDiscoveryDescription", "lost_description")
    }
  };

  const dataPoints = history.reduce((a, c) => {
    const newDataIds = dataIds[c] || [];
    return [...new Set([...a, ...newDataIds])];
  }, new Set());

  const summary = dataPoints.map(dataId => ({
    label: transforms[dataId]?.label,
    question: transforms[dataId]?.question,
    value: transforms[dataId]?.transform?.(progress),
    ...(transforms[dataId]?.media &&
      transforms[dataId]?.media?.(progress) && {
        media: transforms[dataId]?.media?.(progress)
      })
  }));

  return summary;
};

const initialState = {
  currentIndex: 0,
  history: ["whatIs"]
};

const Flow = ({
  transactionId,
  disputedAmount,
  timestamp,
  config,
  state,
  setState,
  setLoading,
  setSubmitted,
  setExiting,
  setError,
  submitDispute,
  isMobile,
  close
}) => {
  const { currentIndex, history } = state;
  const page = history[currentIndex];
  const {
    id,
    title,
    detail,
    prompt,
    tips,
    fields,
    valid,
    next,
    exit,
    submit
  } = config[page];

  const { progress: { [page]: pageState } = {} } = state;

  const updateProgress = ({
    page: p = page,
    state: s = state || {},
    data
  } = {}) => {
    const { progress, progress: { [p]: pageState } = {} } = s;
    setState({
      ...s,
      progress: {
        ...progress,
        [p]: {
          ...pageState,
          ...data
        }
      }
    });
  };

  const setValid = ({ page: p = page, state: s = state, valid } = {}) => {
    updateProgress({ page: p, state: s, data: { valid } });
  };

  const setComplete = ({ page: p = page, state: s = state, complete } = {}) => {
    updateProgress({ page: p, state: s, data: { complete } });
  };

  const push = ({ page: p = page, state: s = state } = {}) => {
    const { currentIndex: i, history: h } = s;
    setState({
      ...s,
      currentIndex: i + 1,
      history: [...h, p]
    });
  };

  const back = ({
    state: s = state,
    page: p = page,
    dropCurrentProgress = true
  } = {}) => {
    const { currentIndex: i, history: h } = s;
    if (i < 1) return;
    let progress = s.progress;
    if (dropCurrentProgress) {
      const { [p]: currentPageProgress, ...rest } = progress || {};
      progress = { ...rest };
    }
    setState({
      ...s,
      currentIndex: i - 1,
      history: [...h.slice(0, -1)],
      progress
    });
  };

  const showBackButton = state.currentIndex > 0;
  const showCancelButton = false;
  const showNextButton = true;

  const allowBack = state.currentIndex > 0;
  const allowCancel = false;
  const allowNext = !valid || !!valid?.(pageState);

  const confirmBack = false;
  const confirmCancel = true;
  const confirmNext = false;

  const handleBack = ({ confirm = confirmBack } = {}) => {
    back();
  };

  const handleCancel = ({ confirm = confirmCancel } = {}) => {
    setExiting(true);
  };

  const handleSubmit = ({ state: s = state } = {}) => {
    console.log("submitting with state", s);
    setLoading(true);
    submitDispute(
      {
        transactionId,
        disputedAmount,
        form: JSON.stringify({
          state: s || {},
          summary: summarize(s || {}) || {}
        })
      },
      () => {
        // go to success scenario
        console.log("submitted successfully");
        setSubmitted(true);
      },
      () => {
        // go to failure scenario
        setError("Failed to submit, please try again later.");
        setLoading(false);
      }
    );
  };

  const handleNext = ({ confirm = confirmNext } = {}) => {
    const { progress: { [page]: pageState = {} } = {} } = state;
    if (!pageState) setState({ ...state, error: "gotta fill it out" });
    else if (exit) close();
    else if (submit) handleSubmit();
    else if (!valid || valid?.(pageState)) {
      setValid({ valid: true });
      setComplete({ complete: true });
      push({ page: next(pageState) });
    } else updateProgress({ data: { error: "Invalid entry" } });
  };

  const renderButtonField = ({
    field,
    state: s = state,
    className = "flex justify-around"
  } = {}) => {
    const { progress = {}, progress: { [page]: pageState = {} } = {} } =
      s || {};
    const { fields: pageFields = {} } = pageState;

    return (
      <div key={field.id} id={field.id} className={className}>
        {field.elements.map(e => (
          <Button
            key={e.id}
            className={`rounded-full`}
            onClick={() => {
              console.log("e.value:", e.value);
              const { value } = e;
              setState({
                ...s,
                progress: {
                  ...progress,
                  [page]: {
                    ...pageState,
                    fields: {
                      ...pageFields,
                      [field.id]: { value }
                    }
                  }
                }
              });
            }}
            green={pageFields?.[field.id]?.value === e.value}
          >
            {e.label}
          </Button>
        ))}
      </div>
    );
  };

  const renderRadioField = ({
    field,
    state: s = state,
    className = ""
  } = {}) => {
    const { progress = {}, progress: { [page]: pageState = {} } = {} } =
      s || {};
    const { fields: pageFields = {} } = pageState;

    return (
      <div key={field.id} id={field.id} className={className}>
        <RadioButtons
          elements={field.elements.map(e => ({
            ...e,
            ...(e.value === pageFields?.[field.id]?.value && {
              selected: true
            })
          }))}
          component={({ element }) => (
            <div className={`${element.disabled ? "text-gray-400" : ""}`}>
              {element.label}
            </div>
          )}
          didSelect={data => {
            if (data.disabled) return;
            const { value } = data;
            setState({
              ...s,
              progress: {
                ...progress,
                [page]: {
                  ...pageState,
                  fields: {
                    ...pageFields,
                    [field.id]: { value }
                  }
                }
              }
            });
          }}
        />
      </div>
    );
  };

  const renderTextField = ({ field } = {}) => {
    const { fields: pageFields = {} } = pageState || {};
    const { elements = [] } = field;
    return (
      <div key={field.id} id={field.id} className="mb-4">
        {elements.map(el => (
          <SettingsInputContainer>
            <Input
              value={pageFields?.[field.id]?.value || ""}
              placeholder={el.placeholder || "Enter text here"}
              onChange={e => {
                const {
                  target: { value }
                } = e;
                updateProgress({
                  data: {
                    fields: {
                      ...pageFields,
                      [field.id]: { value }
                    }
                  }
                });
              }}
            />
          </SettingsInputContainer>
        ))}
      </div>
    );
  };

  const renderNumberField = ({ field } = {}) => {
    const { fields: pageFields = {} } = pageState || {};
    const { elements = [] } = field;

    return (
      <div key={field.id} id={field.id}>
        {elements.map((el, i) => {
          const { type } = el;
          if (type === "currency") {
            return (
              <SettingsInputContainer key={`inputContainer-${i}`}>
                <>
                  <span className="text-gray-500 border-r mr-2 pr-2">$</span>
                  <Input
                    key={`${history[currentIndex]}-${i}`}
                    value={pageFields?.[field.id]?.value || ""}
                    type="text"
                    placeholder="Enter a dollar amount here"
                    onChange={e => {
                      const {
                        target: { value }
                      } = e;
                      if (commafy(value))
                        updateProgress({
                          data: {
                            fields: {
                              ...pageFields,
                              [field.id]: { value: commafy(value) }
                            }
                          }
                        });
                    }}
                  />
                </>
              </SettingsInputContainer>
            );
          } else {
            return (
              <SettingsInputContainer key={`${history[currentIndex]}-${i}`}>
                <Input
                  value={pageFields?.[field.id]?.value || ""}
                  type="number"
                  placeholder="Enter a number here"
                  onChange={e => {
                    const {
                      target: { value }
                    } = e;
                    updateProgress({
                      data: {
                        fields: {
                          ...pageFields,
                          [field.id]: { value }
                        }
                      }
                    });
                  }}
                />
              </SettingsInputContainer>
            );
          }
        })}
      </div>
    );
  };

  const renderDateField = ({ field } = {}) => {
    const { fields: pageFields = {} } = pageState || {};
    const { elements: e = [{ id: "default" }] } = field;
    return (
      <div key={field.id} id={field.id}>
        {e.map((e, i) => (
          <div key={`${field.id}-${i}`} className="flex justify-center">
            <DateInput
              disableFuture={e.disableFuture}
              minDate={e.minDate || moment(timestamp)}
              className="m-auto"
              onChange={value => {
                updateProgress({
                  data: {
                    fields: {
                      ...pageFields,
                      [field.id]: { value: value.format("MM/DD/YYYY") }
                    }
                  }
                });
              }}
              onError={e => {
                if (e && pageFields?.[field.id]?.value)
                  updateProgress({
                    data: {
                      fields: {
                        ...pageFields,
                        [field.id]: { value: null }
                      }
                    }
                  });
              }}
              initialValue={pageFields?.[field.id]?.value || null}
              placeholder={"MM/DD/YYYY"}
            />
          </div>
        ))}
      </div>
    );
  };

  const renderTextAreaField = ({ field, pageState } = {}) => {
    const { fields: pageFields = {} } = pageState || {};
    const { elements: e = [] } = field;
    return (
      <div key={field.id} id={field.id}>
        {e.map((e, i) => (
          <TextArea
            key={`${field.id}-${i}`}
            value={pageFields?.[field.id]?.value || ""}
            placeholder="Enter a description here"
            className="border border-gray-400 p-2"
            onChange={e => {
              const {
                target: { value }
              } = e;
              updateProgress({
                data: {
                  fields: {
                    ...pageFields,
                    [field.id]: { value }
                  }
                }
              });
            }}
            maxLength={e.maxLength}
          />
        ))}
      </div>
    );
  };

  const renderToggleField = ({ field } = {}) => {
    const { fields: pageFields = {} } = pageState || {};
    const { elements: e = [] } = field;
    return (
      <div key={field.id} id={field.id}>
        {e.map(e => (
          <>
            <Toggle
              active={!!pageFields?.[field.id]?.value}
              onChange={e => {
                const {
                  target: { checked }
                } = e;
                updateProgress({
                  data: {
                    fields: {
                      ...pageFields,
                      [field.id]: { value: !!checked }
                    }
                  }
                });
              }}
            />
            {e.label && <span className="ml-4">{e.label}</span>}
          </>
        ))}
      </div>
    );
  };

  const renderCheckboxField = ({ field } = {}) => {
    const { fields: pageFields = {} } = pageState || {};
    const { elements: e = [] } = field;
    return (
      <div key={field.id} id={field.id} className="mb-4">
        {e.map(e => (
          <Checkbox
            label={e.label}
            checked={!!pageFields?.[field.id]?.value}
            onChange={e => {
              const {
                target: { checked }
              } = e;
              updateProgress({
                data: {
                  fields: {
                    ...pageFields,
                    [field.id]: { value: !!checked || null }
                  }
                }
              });
            }}
          />
        ))}
      </div>
    );
  };

  const renderUploadField = ({ field } = {}) => {
    const { fields: pageFields = {} } = pageState || {};
    const { elements: e = [] } = field;
    const fileName = pageFields?.[field.id]?.description;
    const override = !!pageFields?.[field.id]?.value;
    return (
      <div key={field.id} id={field.id} className="mb-4">
        {e.map((e, i) => (
          <Upload
            key={`${history[currentIndex]}-${i}`}
            overrideFileName={fileName}
            overrideComplete={override}
            maxSize={(e.maxFileSize || 1) * 1048576}
            multiple={e.multiple}
            onError={data => console.log("upload error:", data)}
            onComplete={data => {
              if (override) return;
              console.log("upload complete with data:", data);
              const { key, fileName } = data;
              updateProgress({
                data: {
                  fields: {
                    ...pageFields,
                    [field.id]: { value: key, description: fileName }
                  }
                }
              });
            }}
          />
        ))}
      </div>
    );
  };

  const fieldRenderers = {
    button: renderButtonField,
    checkbox: renderCheckboxField,
    date: renderDateField,
    number: renderNumberField,
    radio: renderRadioField,
    text: renderTextField,
    textarea: renderTextAreaField,
    toggle: renderToggleField,
    upload: renderUploadField,
    unsupported: () => <div>Unsupported field type</div>
  };

  const renderFields = ({ fields, mobile, pageState } = {}) =>
    (fields || []).map(field =>
      (fieldRenderers[field?.type] || fieldRenderers.unsupported)({
        field,
        mobile,
        pageState
      })
    );

  const renderTitle = ({ title: t = title } = {}) => (
    <p className="mb-6 font-semibold text-xl text-center">
      {t || "Open a New Dispute"}
    </p>
  );

  const renderTips = ({ tips, mobile: m } = {}) =>
    tips.map(t => (
      <div
        className={`mb-4 px-${
          m ? 4 : 6
        } border rounded py-2 hover:bg-white select-none`}
      >
        <span className="fas fa-hand-point-right mr-2" />
        {t}
      </div>
    ));

  const renderPrompt = ({ prompt: p, mobile: m } = {}) => (
    <div className={`mb-4 text-center px-${m ? 4 : 6} font-bold text-lg`}>
      {p}
    </div>
  );

  const renderDetail = ({ detail: d = () => {}, mobile: m } = {}) => (
    <div className={`mb-4 text-center px-${m ? 4 : 6}`}>{d()}</div>
  );

  const renderReview = ({ state: s = state, mobile: m } = {}) => {
    const summary = summarize(s);
    return (
      <div
        className={`mb-4 ${!m ? "overflow-auto" : ""}`}
        style={{ ...(!m && { maxHeight: 500 }) }}
      >
        {summary.map(i => {
          return (
            <Accordion allowZeroExpanded={true}>
              <div className="accordion-wrap">
                <AccordionItem>
                  <AccordionItemHeading>
                    <AccordionItemButton>{i.label}</AccordionItemButton>
                  </AccordionItemHeading>
                  <AccordionItemPanel>
                    {<p>{i.value || "No information provided"}</p>}
                  </AccordionItemPanel>
                </AccordionItem>
              </div>
            </Accordion>
          );
        })}
      </div>
    );
  };

  const renderBody = ({
    detail: d = detail,
    prompt: p = prompt,
    tips: t = tips,
    fields: f = fields,
    submit: s = submit,
    isMobile: m = isMobile
  } = {}) => (
    <div>
      {d && renderDetail({ detail: d, mobile: m })}
      {s && renderReview({ mobile: m })}
      {p && renderPrompt({ prompt: p, mobile: m })}
      {t && renderTips({ tips: t, mobile: m })}
      {f && renderFields({ fields: f, mobile: m, pageState })}
    </div>
  );

  const renderPageError = () => {
    const { error } = pageState || {};
    return error && <div className="text-red-500 -mb-4 mt-2">{error}</div>;
  };

  const renderNavigationButtons = ({
    showBackButton: sb = showBackButton,
    allowBack: ab = allowBack,
    handleBack: hb = handleBack,
    showCancelButton: sc = showCancelButton,
    allowCancel: ac = allowCancel,
    handleCancel: hc = handleCancel,
    showNextButton: sn = showNextButton,
    allowNext: an = allowNext,
    handleNext: hn = handleNext,
    submit: s = submit,
    exit: e = exit
  } = {}) => (
    <div
      className={`mt-8 flex ${sb || sc ? `justify-between` : `justify-end`}`}
    >
      {[
        ["Back", sb, ab, hb],
        ["Cancel", sc, ac, hc],
        [e ? "Exit" : s ? "Submit" : "Next", sn, an, hn]
      ].map(
        ([label, show, allow, handle]) =>
          show && (
            <div key={label}>
              <Button
                disabled={!allow}
                onClick={handle}
                style={{ padding: "0.25em 1.25em" }}
                overrideStyle={label == "Cancel"}
                className={
                  label == "Cancel"
                    ? "bg-red-300 hover:bg-red-500 text-white border-red-300"
                    : // ? "text-gray-400 hover:text-white border hover:border-none hover:bg-red-500"
                      ""
                }
              >
                {label}
              </Button>
            </div>
          )
      )}
    </div>
  );

  // const Screen = () => (
  //   <div>
  //     {renderTitle()}
  //     {renderBody()}
  //     {renderPageError()}
  //     {renderNavigationButtons()}
  //   </div>
  // );

  // return <Screen />;
  return (
    <div>
      {renderTitle()}
      {renderBody()}
      {renderPageError()}
      {renderNavigationButtons()}
    </div>
  );
};

const DisputeFlow = ({ data, close, submitDispute, next }) => {
  const [state, setState] = useState(initialState);
  const [started, setStarted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [exiting, setExiting] = useState(false);
  const { isMobile } = useContext(MobileContext);

  const { user, transaction } = data || {};
  const { firstName, lastName } = user || {};
  const { id: transactionId, amount, created_at: timestamp } =
    transaction || {};

  const missingData = !firstName || !lastName || !amount || !timestamp;
  const [error, setError] = useState(missingData ? "Missing data" : null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [disputedAmount, setDisputedAmount] = useState(
    amount > 0 ? amount : null
  );

  useEffect(() => {
    if (submitted) {
      setLoading(false);
      next();
    }
  }, [submitted]);

  useEffect(() => {
    setErrorMessage(
      !disputedAmount
        ? "Invalid dispute amount"
        : disputedAmount > amount
        ? "Dispute amount cannot be greater than transaction amount"
        : disputedAmount <= 0
        ? "Dispute amount must be greater than zero"
        : null
    );
  }, [disputedAmount]);

  track("DASHBOARD_TRANSACTIONS_DISPUTE_SCREEN_LOADED");

  const renderLoading = () => (
    <div>
      <div>
        <p className="mb-6 font-semibold text-xl text-center">
          Open a New Dispute
        </p>
        <div
          className="loader items-center justify-center m-auto"
          style={{ height: "80px", width: "80px" }}
        />
        <p className={`my-4 text-center px-${isMobile ? 4 : 6}`}>
          Submitting your dispute...
        </p>
      </div>
    </div>
  );

  const renderError = () => (
    <div>
      <div>
        <p className="mb-6 font-semibold text-xl text-center">
          Open a New Dispute
        </p>
        <p className={`mb-4 text-center px-${isMobile ? 4 : 6}`}>
          Something Went Wrong
        </p>
        <div className={error ? "text-red-600 my-2" : "hidden"}>{error}</div>
      </div>
      <div className="mt-4 flex justify-between">
        <button type="button" onClick={close}>
          Close
        </button>
        <Button
          onClick={() => setError(null)}
          style={{ padding: "0.25em 1.25em" }}
        >
          Go Back
        </Button>
      </div>
    </div>
  );

  const renderConfirmExit = () => (
    <div>
      <div>
        <p className="mb-6 font-semibold text-xl text-center">
          Cancel Dispute Submission
        </p>
        <div
          className={`mb-4 text-center px-${
            isMobile ? 4 : 6
          } font-bold text-lg`}
        >
          Are you sure?
        </div>
        <p className={`mb-4 text-center px-${isMobile ? 4 : 6}`}>
          If you exit now, you will lose all progress and will have to start the
          dispute submission process from the beginning
        </p>
      </div>
      <div className="mt-4 flex justify-around">
        <Button
          onClick={() => setExiting(false)}
          style={{ padding: "0.25em 1.25em" }}
        >
          Go Back
        </Button>
        <Button
          onClick={close}
          style={{ padding: "0.25em 1.25em" }}
          overrideStyle
          className={"bg-red-500 text-white border-red-300 font-bold"}
        >
          Cancel Submission
        </Button>
      </div>
    </div>
  );

  const renderInitial = ({ name, date, amount }) => {
    const handleDisputeAmountChanged = e => {
      const { value } = e.target || {};
      const number = Number(e.target.value);
      if (!isNaN(number))
        setDisputedAmount(
          value.length < number.toFixed(2).length // TODO: handle spaces and 01, try num = num.slice(0, (num.indexOf("."))+3);
            ? value ?? number
            : number.toFixed(2)
        );
    };
    return (
      <div>
        <div>
          <p className="mb-6 font-semibold text-xl text-center">
            Open a New Dispute
          </p>
          <div className="font-bold text-xl">Start With the Merchant First</div>
          <div className="py-2">
            <div>
              Working directly with the merchant is often the fastest way to get
              your money back. It can take TomoCredit more than 60 days to
              resolve a dispute with the merchant.
            </div>
            <div className="pt-2">
              Many companies are changing their policies to be more flexible due
              to recent restrictions, with some allowing you to change or cancel
              plans at no fee and/or to receive credits for future services. We
              recommend you visit their websites to see what’s available for
              your situation.
            </div>
          </div>
          <div className="font-bold text-xl">Contacting TomoCredit</div>
          <div>
            <div>
              We understand the urgency of changing your plans and the
              frustration of paying for a trip or event that has been
              rescheduled or canceled.
            </div>
            <div className="pt-2">
              If you want to speak to TomoCredit about a purchase you made,
              please know that we’re experiencing longer than normal call wait
              times. If you don’t want to wait on the phone:
            </div>
            <ul className="pt-2 ml-4 list-disc">
              <li>
                You can reach out to the merchant - working directly with them
                is often the fastest way to resolve your dispute.
              </li>
              <li>
                You can call TomoCredit later - you can dispute a charge even
                after the planned service date.
              </li>
            </ul>
          </div>
          <div className={errorMessage ? "text-red-600 my-2" : "hidden"}>
            {errorMessage}
          </div>
        </div>
        <div className="mt-4 flex justify-between">
          <Button
            onClick={close}
            overrideStyle
            className={"bg-red-300 hover:bg-red-500 text-white border-red-300"}
          >
            Cancel
          </Button>
          <Button
            disabled={!disputedAmount || errorMessage}
            onClick={() => setStarted(true)}
          >
            Next
          </Button>
        </div>
      </div>
    );
  };

  const renderSubmitted = () => (
    <div>
      <div>
        <p className="mb-6 font-semibold text-xl text-center">
          Open a New Dispute
        </p>
        <p className={`mb-4 text-center px-${isMobile ? 4 : 6}`}>
          Your dispute has been submitted
        </p>
      </div>
      <div className="mt-4 flex justify-between">
        <button type="button" onClick={close}>
          Done
        </button>
      </div>
    </div>
  );

  return error ? (
    renderError()
  ) : loading ? (
    renderLoading()
  ) : exiting ? (
    renderConfirmExit()
  ) : submitted ? (
    renderSubmitted()
  ) : started ? (
    // ? renderFlow({ state, setState, config })
    <Flow
      transactionId={transactionId}
      disputedAmount={disputedAmount}
      timestamp={timestamp}
      config={config}
      state={state}
      setState={setState}
      setLoading={setLoading}
      setSubmitted={setSubmitted}
      setExiting={setExiting}
      setError={setError}
      submitDispute={submitDispute}
      isMobile={isMobile}
      close={close}
    />
  ) : (
    renderInitial({
      name: firstName && lastName ? `${firstName} ${lastName}` : "unknown",
      date: timestamp ? moment(timestamp).format("MM/DD/YYYY") : "unknown",
      amount: amount ? `${amount}` : "unknown"
    })
  );
};

export default withStyles(DisputeFlow);
